/**
 * RunControl - Control component for selecting runs
 *
 * Supports two modes:
 * - Card variant: Shows run info in an expandable card (uses RunSelectorDropdown with card mode)
 * - Standard variant: Shows run dropdown inline (uses RunSelectorDropdown in standard mode)
 * - Label variant: Static display of run info (no dropdown)
 */

import React, {useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {useLayoutConfig} from '../layout/contexts/LayoutConfigContext';
import {RunSelectorDropdown} from '../modal/RunSelectorDropdown';
import {CARD_DEFAULT_CLASSNAMES} from './cardStyles';
import {RunDisplayWithColor} from './RunDisplayWithColor';
import {Title} from './Title';
import {type RunControlProps} from './types';

export type {RunControlProps};

/**
 * RunControl with flexible display modes.
 *
 * Uses RunSelectorDropdown internally for all interactive modes.
 */
export const RunControl: React.FC<RunControlProps> = ({
  control,
  index,
  columnIndex,
  rowIndex,
  disableRunLinks,
  showColorPill = false,
}) => {
  const layoutConfig = useLayoutConfig();

  // Get available runs and selected run from context
  const availableRuns = useMemo(
    () => layoutConfig?.getAvailableRuns(columnIndex) ?? [],
    [layoutConfig, columnIndex]
  );
  const selectedRunName = layoutConfig?.getRun(columnIndex, rowIndex);

  // Find the selected run object
  const selectedRun = useMemo(() => {
    if (!selectedRunName) {
      return null;
    }
    const run = availableRuns.find(r => r.name === selectedRunName) ?? null;
    if (!run) {
      return availableRuns[0] ?? null;
    }
    return run;
  }, [availableRuns, selectedRunName]);

  const defaultClassNames = twMerge(
    CARD_DEFAULT_CLASSNAMES,
    control.classNames
  );

  const handleRunSelect = (run: RunWithHistoryAndMediaWandb | null) => {
    if (run && layoutConfig) {
      layoutConfig.setRun(run.name, columnIndex);
    }
  };

  // Helper to render run display without label
  const renderRunDisplay = (
    run: RunWithHistoryAndMediaWandb | null,
    disableLink?: boolean
  ) => {
    if (!run) {
      return null;
    }

    const runSignature = {
      entityName: run.entityName,
      projectName: run.projectName,
      runName: run.name,
    };
    const displayName = run.displayName ?? run.name;

    return (
      <RunDisplayWithColor
        displayName={displayName}
        color={run.color}
        runSignature={runSignature}
        disableLink={disableLink}
        showColorPill={showColorPill}
        textClassName="text-center"
      />
    );
  };

  // CASE 1: Label type (static display)
  // Labels are always rendered as static text, regardless of variant
  if (control.type === 'label') {
    if (!selectedRunName || !selectedRun) {
      return null;
    }

    // Always show just the run display without the "Run" label
    return renderRunDisplay(selectedRun, disableRunLinks);
  }

  // CASE 2: Component type (interactive dropdown)
  // @ts-ignore - variant field might not exist on old type yet
  const shouldUseCard = control.variant === 'card';

  if (shouldUseCard) {
    if (!selectedRun) {
      return null;
    }

    return (
      <div
        className={defaultClassNames}
        data-test={`run-control-${index}-card`}>
        <RunSelectorDropdown
          size="small"
          selectedRun={selectedRun}
          onRunSelect={handleRunSelect}
          runsWithMedia={availableRuns}
          useCardMode={true}
          showColorPill={showColorPill}
          renderTitle={(run: RunWithHistoryAndMediaWandb | null) => {
            if (!run) {
              return null;
            }
            const runSignature = {
              entityName: run.entityName,
              projectName: run.projectName,
              runName: run.name,
            };
            return (
              <Title
                label="Run"
                value={
                  <RunDisplayWithColor
                    displayName={run.displayName ?? run.name}
                    color={run.color}
                    runSignature={runSignature}
                    disableLink={true}
                    showColorPill={showColorPill}
                  />
                }
                labelPosition="separated"
              />
            );
          }}
          placeholder="Search runs..."
          menuItemClasses="text-sm/6 text-wb-text-primary"
        />
      </div>
    );
  }

  // Standard variant (workspace view - direct dropdown)
  // Show dropdown directly without "Run" label
  return (
    <div className={defaultClassNames} data-test={`run-control-${index}`}>
      <div className="mt-1">
        <RunSelectorDropdown
          size="small"
          runsWithMedia={availableRuns}
          selectedRun={selectedRun}
          onRunSelect={handleRunSelect}
          valueClasses="text-sm/8"
          menuItemClasses="text-sm/6 text-wb-text-primary"
          showColorPill={showColorPill}
        />
      </div>
    </div>
  );
};
